/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { DatePicker, localToday, useFlags } from 'sarsaparilla';
import get from 'lodash/get';
import { parseDate } from '@internationalized/date';
import { getClassName, ANNUAL_PERMIT_TYPES } from '../../utils/utils';
import { PERMIT_IN_PROGRESS } from '../../constants';
import { issuanceStatuses } from '../../utils/permitUtils';
import PermitType from './PermitType';

export default function EntryDateSelector({
    permit,
    reservation,
    validation,
    pActions,
    displayValidationError,
    addToInputRefs,
    isInternal,
    vIndex,
}) {
    const setStartDate = (date) => {
        if (!date) {
            pActions.updatePendingReservation(reservation.id, 'start_date', null);
            pActions.updatePendingReservation(reservation.id, 'end_date', null);

            return;
        }

        const permitType =
            permit.permit_types_list?.find(
                (types) => types.id === reservation.permit_type
            ) ?? {};

        pActions.updatePendingReservation(reservation.id, 'start_date', date.toString());

        if (permitType.stay_length > 0) {
            let lengthUnit = 'days';
            if (permitType.stay_length_increment === 'Month') {
                lengthUnit = 'months';
            }

            const endDate = date.add({ [lengthUnit]: permitType.stay_length - 1 });

            pActions.updatePendingReservation(
                reservation.id,
                'end_date',
                endDate.toString()
            );
        }
    };

    const startDateStr = get(reservation, 'start_date', '');
    const startDate = startDateStr ? parseDate(startDateStr) : null;

    const { permitVehicleStartDateModificationAllowedPermitIds } = useFlags();
    const isPermitAllowedToModifyStartDate =
        permitVehicleStartDateModificationAllowedPermitIds
            ?.split('|')
            .includes(permit.id);
    const isInternalStartDateModifiable = isInternal && isPermitAllowedToModifyStartDate;

    const isInProgress =
        get(reservation, 'status', issuanceStatuses.get(PERMIT_IN_PROGRESS)) ===
        issuanceStatuses.get(PERMIT_IN_PROGRESS);
    const isAnualPermitType = ANNUAL_PERMIT_TYPES.includes(
        get(reservation, 'division_id', '')
    );

    let isDisabled = !isInProgress || isAnualPermitType;
    if (!isInProgress) {
        isDisabled = !isInternalStartDateModifiable;
    }

    const entryDateValidation = getClassName(
        get(validation, `${reservation.id}.start_date`),
        displayValidationError
    );

    return (
        <PermitType reservation={reservation} permit={permit}>
            <div className={`form-entry-date ${entryDateValidation}`}>
                <DatePicker
                    isRequired
                    style={{ width: '18.8rem' }}
                    id={`entry-date-${reservation.id}`}
                    ref={addToInputRefs(vIndex + 10)}
                    isDisabled={isDisabled}
                    label="Entry Date"
                    value={startDate}
                    onChange={setStartDate}
                    minValue={isInProgress ? localToday : null}
                />
            </div>
        </PermitType>
    );
}

EntryDateSelector.propTypes = {
    permit: PropTypes.object.isRequired,
    reservation: PropTypes.object.isRequired,
    validation: PropTypes.object.isRequired,
    pActions: PropTypes.object.isRequired,
    displayValidationError: PropTypes.bool,
    addToInputRefs: PropTypes.any,
    vIndex: PropTypes.number,
};
